import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './ui';
import { TwitterIcon, LinkedinIcon, MailIcon, PresentationIcon, ArrowRightIcon, FileTextIcon, BuildingIcon, BriefcaseIcon, UserCircleIcon, HandshakeIcon, Building2Icon, InfoIcon } from 'lucide-react';
type LinkItem = {
  label: string;
  href: string;
};
type Column = {
  key: string;
  title: string;
  caption: string;
  icon: React.FC<{
    className?: string;
  }>;
  links: LinkItem[];
};
const COLUMNS: Column[] = [{
  key: 'operators',
  title: 'For Operators',
  caption: 'Hotels & sales teams',
  icon: BuildingIcon,
  links: [{
    label: 'AI Offsite Designer',
    href: '/designer'
  }, {
    label: 'Live Operator Dashboard',
    href: '/dashboard'
  }, {
    label: 'Client Book & Revenue Intel',
    href: '/dashboard/clients'
  }, {
    label: 'AI Proposal Builder',
    href: '/dashboard/proposals/new'
  }, {
    label: 'Sample Post-Event Report',
    href: '/report'
  }, {
    label: 'Sales Sheet',
    href: '/sales-sheet'
  }]
}, {
  key: 'clients',
  title: 'For Clients',
  caption: 'The buyer / organizer',
  icon: BriefcaseIcon,
  links: [{
    label: 'Client Portal Overview',
    href: '/client-portal'
  }, {
    label: 'Diagnostic Intake',
    href: '/client-portal/diagnostic'
  }, {
    label: 'AI Offsite Brief',
    href: '/client-portal/brief'
  }, {
    label: 'Agenda Builder',
    href: '/client-portal/agenda'
  }, {
    label: 'Deliverables Center',
    href: '/client-portal/deliverables'
  }, {
    label: 'Outcomes Dashboard',
    href: '/client-portal/outcomes'
  }]
}, {
  key: 'participants',
  title: 'For Participants',
  caption: 'Attendees on the day',
  icon: UserCircleIcon,
  links: [{
    label: 'Experience Hub',
    href: '/experience-hub'
  }, {
    label: 'Flight Pack',
    href: '/experience-hub/flight-pack'
  }, {
    label: 'Arrival Briefing',
    href: '/experience-hub/arrival'
  }, {
    label: 'Live Companion',
    href: '/experience-hub/live'
  }, {
    label: 'Knowledge Studio',
    href: '/experience-hub/knowledge'
  }, {
    label: 'Action Plan',
    href: '/experience-hub/action-plan'
  }]
}, {
  key: 'partners',
  title: 'For Partners',
  caption: 'DMCs · resellers · facilitators',
  icon: HandshakeIcon,
  links: [{
    label: 'Partner Portal',
    href: '/partner-portal'
  }, {
    label: 'Book Analyzer',
    href: '/partner-portal/book-analyzer'
  }, {
    label: 'Pipeline',
    href: '/partner-portal/pipeline'
  }, {
    label: 'Proposal Builder',
    href: '/partner-portal/proposals'
  }, {
    label: 'Performance',
    href: '/partner-portal/performance'
  }]
}, {
  key: 'industries',
  title: 'Industries',
  caption: 'Solution by vertical',
  icon: Building2Icon,
  links: [{
    label: 'Hotels & Resorts',
    href: '/for-hotels'
  }, {
    label: 'Health Clubs & Spas',
    href: '/for-health-clubs'
  }, {
    label: 'DMCs',
    href: '/for-dmcs'
  }, {
    label: 'MICE Planners',
    href: '/for-mice'
  }, {
    label: 'Corporate HR',
    href: '/for-hr'
  }, {
    label: 'Retreat Facilitators',
    href: '/for-facilitators'
  }]
}, {
  key: 'company',
  title: 'Company',
  caption: 'About & resources',
  icon: InfoIcon,
  links: [{
    label: 'Meet Us at HITEC',
    href: '/hitec'
  }, {
    label: 'How It Works',
    href: '/#how-it-works'
  }, {
    label: 'Pricing',
    href: '/#pricing'
  }, {
    label: 'Sample Report',
    href: '/report'
  }, {
    label: 'Product Requirements',
    href: '/prd'
  }, {
    label: 'Investor Deck',
    href: '/investor-deck'
  }, {
    label: 'Lamplighter Pitch',
    href: '/lamplighter-deck'
  }]
}];
const LEGAL_LINKS: LinkItem[] = [{
  label: 'Privacy',
  href: '#'
}, {
  label: 'Terms',
  href: '#'
}, {
  label: 'Security',
  href: '#'
}, {
  label: 'Cookies',
  href: '#'
}];
function FooterLink({
  link
}: {
  link: LinkItem;
}) {
  const isInternal = link.href.startsWith('/') && !link.href.includes('#');
  if (isInternal) {
    return <Link to={link.href} className="text-sm text-slate-500 hover:text-brand-navy transition-colors" data-id="element-6686">
        {link.label}
      </Link>;
  }
  return <a href={link.href} className="text-sm text-slate-500 hover:text-brand-navy transition-colors" data-id="element-6687">
      {link.label}
    </a>;
}
export function SiteFooter({
  hideDeckCtas = false
}: {
  hideDeckCtas?: boolean;
}) {
  return <footer className="bg-white border-t border-slate-100" data-id="element-6688">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-id="element-6689">
        {/* Prominent Pitch Deck CTAs */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-16 pb-12 border-b border-slate-100 ${hideDeckCtas ? 'hidden' : ''}`} data-id="element-6690">
          <Link to="/investor-deck" className="group bg-[#0A0F1F] text-white rounded-2xl p-6 flex items-center justify-between hover:shadow-xl transition-all duration-300" data-id="element-6691">
            <div className="flex items-center gap-4 min-w-0" data-id="element-6692">
              <div className="w-11 h-11 shrink-0 rounded-full bg-white/10 flex items-center justify-center" data-id="element-6693">
                <PresentationIcon className="w-5 h-5 text-brand-teal" data-id="element-6694" />
              </div>
              <div className="min-w-0" data-id="element-6695">
                <div className="text-[10px] font-bold text-brand-teal uppercase tracking-wider mb-0.5" data-id="element-6696">
                  Seed Round 2026
                </div>
                <div className="font-serif-display text-xl font-bold leading-tight" data-id="element-6697">
                  Investor Deck
                </div>
              </div>
            </div>
            <div className="w-9 h-9 shrink-0 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-brand-coral transition-colors" data-id="element-6698">
              <ArrowRightIcon className="w-4 h-4" data-id="element-6699" />
            </div>
          </Link>

          <Link to="/lamplighter-deck" className="group bg-[#FAF6EF] text-[#0B1B3B] rounded-2xl p-6 flex items-center justify-between hover:shadow-xl transition-all duration-300 border border-[#B6824B]/20" data-id="element-6700">
            <div className="flex items-center gap-4 min-w-0" data-id="element-6701">
              <div className="w-11 h-11 shrink-0 rounded-full bg-[#B6824B]/10 flex items-center justify-center" data-id="element-6702">
                <PresentationIcon className="w-5 h-5 text-[#B6824B]" data-id="element-6703" />
              </div>
              <div className="min-w-0" data-id="element-6704">
                <div className="text-[10px] font-bold text-[#B6824B] uppercase tracking-wider mb-0.5" data-id="element-6705">
                  Hotel Pitch
                </div>
                <div className="font-serif-display text-xl font-bold leading-tight" data-id="element-6706">
                  Lamplighter Inn
                </div>
              </div>
            </div>
            <div className="w-9 h-9 shrink-0 rounded-full bg-[#0B1B3B]/5 flex items-center justify-center group-hover:bg-[#0B1B3B] group-hover:text-white transition-colors" data-id="element-6707">
              <ArrowRightIcon className="w-4 h-4" data-id="element-6708" />
            </div>
          </Link>
        </div>

        {/* Brand row */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12 pb-12 border-b border-slate-100" data-id="element-6709">
          <div className="lg:col-span-2 max-w-md" data-id="element-6710">
            <Logo data-id="element-6711" />
            <p className="text-sm text-slate-500 mt-4 leading-relaxed" data-id="element-6712">
              The AI-powered experience layer for meetings, retreats, and group
              stays — purpose-built for the operators who sell them, the clients
              who buy them, and the participants who live them.
            </p>
            <div className="flex gap-3 mt-6" data-id="element-6713">
              <a href="#" aria-label="Twitter" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-brand-navy hover:text-white transition-colors" data-id="element-6714">
                <TwitterIcon className="w-4 h-4" data-id="element-6715" />
              </a>
              <a href="#" aria-label="LinkedIn" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-brand-navy hover:text-white transition-colors" data-id="element-6716">
                <LinkedinIcon className="w-4 h-4" data-id="element-6717" />
              </a>
              <a href="mailto:hello@offsiteplus.com" aria-label="Email" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-brand-navy hover:text-white transition-colors" data-id="element-6718">
                <MailIcon className="w-4 h-4" data-id="element-6719" />
              </a>
            </div>
          </div>

          {/* Two quick legend tiles to teach who-uses-what */}
          <div className={`lg:col-span-2 grid grid-cols-2 gap-3 ${hideDeckCtas ? 'hidden' : ''}`} data-id="element-6720">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-6721">
              <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-1.5" data-id="element-6722">
                One platform · 4 audiences
              </div>
              <p className="text-xs text-slate-600 leading-relaxed" data-id="element-6723">
                Each column below maps to a real product surface — Operators run
                it, Clients shape it, Participants live it, Partners resell it.
              </p>
            </div>
            <div className="bg-brand-navy text-white rounded-xl p-4" data-id="element-6724">
              <div className="text-[10px] font-bold uppercase tracking-wider text-brand-teal mb-1.5" data-id="element-6725">
                Quick start
              </div>
              <Link to="/designer" className="text-sm font-bold leading-snug hover:text-brand-teal transition-colors inline-flex items-center gap-1.5" data-id="element-6726">
                Try the AI Designer
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-6727" />
              </Link>
            </div>
          </div>
        </div>

        {/* Audience-organized link columns */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-10 mb-12" data-id="element-6728">
          {COLUMNS.map(col => {
          const Icon = col.icon;
          return <div key={col.key} data-id="element-6729">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-6730">
                  <Icon className="w-3.5 h-3.5 text-brand-coral" data-id="element-6731" />
                  <h4 className="text-xs font-bold text-brand-navy uppercase tracking-wider" data-id="element-6732">
                    {col.title}
                  </h4>
                </div>
                <div className="text-[10px] font-medium text-slate-400 mb-4 uppercase tracking-wider" data-id="element-6733">
                  {col.caption}
                </div>
                <ul className="space-y-2.5" data-id="element-6734">
                  {col.links.map(link => <li key={link.label} data-id="element-6735">
                      <FooterLink link={link} data-id="element-6736" />
                    </li>)}
                </ul>
              </div>;
        })}
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-6737">
          <div className="text-sm text-slate-400" data-id="element-6738">
            © {new Date().getFullYear()} Offsite+. All rights reserved.
          </div>
          <div className="flex items-center gap-5 flex-wrap justify-center" data-id="element-6739">
            {LEGAL_LINKS.map(l => <a key={l.label} href={l.href} className="text-xs text-slate-400 hover:text-brand-navy transition-colors" data-id="element-6740">
                {l.label}
              </a>)}
            <a href="mailto:hello@offsiteplus.com" className="text-xs text-slate-400 hover:text-brand-navy transition-colors" data-id="element-6741">
              Contact
            </a>
          </div>
          <div className="text-xs text-slate-400 font-medium" data-id="element-6742">
            Built for hotels, DMCs, MICE planners & corporate teams.
          </div>
        </div>
      </div>
    </footer>;
}